import { type Dispatch, type SetStateAction, useEffect, useRef, useState } from 'react';

import { useLocalStorage } from '@mantine/hooks';
import { useNavigate } from 'react-router-dom';

export type LayoutShortcuts = {
  logModalOpened: boolean;
  setLogModalOpened: Dispatch<SetStateAction<boolean>>;
  uploadFeatureUnlocked: boolean;
  /**
   * The agent panel. It stays closed until the person types AGENT, while the
   * agent is new, and the choice outlasts a reload.
   */
  agentOpened: boolean;
  setAgentOpened: (opened: boolean) => void;
};

export function useLayoutShortcuts(): LayoutShortcuts {
  const navigate = useNavigate();
  // A ref, not state: what a sequence sets off runs in the key handler, never
  // inside a state updater, which React may call during render.
  const keySequence = useRef('');
  const [logModalOpened, setLogModalOpened] = useState(false);
  const [uploadFeatureUnlocked, setUploadFeatureUnlocked] = useState(false);
  const [agentOpened, setAgentOpened] = useLocalStorage({
    key: 'teo.agent.opened',
    defaultValue: false,
    getInitialValueInEffect: false,
  });

  // Secret key sequence detection for TEO, UPLOAD and AGENT
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Only track if focus is not on an input element
      if (
        event.target instanceof HTMLInputElement ||
        event.target instanceof HTMLTextAreaElement ||
        (event.target instanceof HTMLElement && event.target.contentEditable === 'true')
      ) {
        return;
      }

      const key = event.key.toUpperCase();
      const sequence = (keySequence.current + key).slice(-6);
      keySequence.current = '';

      if (sequence.slice(-3) === 'TEO') {
        setLogModalOpened(true);
      } else if (sequence.slice(-5) === 'AGENT') {
        setAgentOpened(true);
      } else if (sequence === 'UPLOAD') {
        setUploadFeatureUnlocked(true);
        void navigate('/upload');
      } else {
        keySequence.current = sequence;
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [navigate, setAgentOpened]);

  return {
    logModalOpened,
    setLogModalOpened,
    uploadFeatureUnlocked,
    agentOpened,
    setAgentOpened,
  };
}
